// DiaryPage — the diary selector at /diary.
//
// First step of the diary flow: pick a driver + a week, then navigate to
// /diary/results?driver_id=&week_start=. No API call on submit; the results
// page does the heavy lifting.
//
// Mirrors UI_AUDIT §2.2 closely — staff use this daily and a layout change
// would disorient them.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Plus,
  Search,
  AlertTriangle,
} from 'lucide-react';
import { Card } from '../../components/ui/Card/Card';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Button } from '../../components/ui/Button/Button';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { Select, type SelectOption } from '../../components/form/Select';
import { DatePicker } from '../../components/form/DatePicker';
import { useToast } from '../../hooks/useToast';
import { useAuthStore } from '../../store/authStore';
import { useDriverHolidayCheck } from '../../hooks/useDriverHolidayCheck';
import { periodLabel } from '../../lib/holidayPeriod';
import { encryptParams } from '../../utils/urlParams';
import { useDiaryDrivers } from './hooks/useDiaryDrivers';
import styles from './DiaryPage.module.css';

function DiaryHeaderActions(): JSX.Element {
  const navigate = useNavigate();
  return (
    <>
      <Button variant="primary" icon={Plus} onClick={() => navigate('/collections/bookings/create')}>
        New Booking
      </Button>
      <Button variant="secondary" icon={Search} onClick={() => navigate('/collections/lookup')}>
        Look Up
      </Button>
    </>
  );
}

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
const MAX_WEEKS_AHEAD = 4;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseISO(s: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Snap any date to the Monday of that ISO-week (Mon–Sun). */
export function mondayOf(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay(); // 0=Sun, 1=Mon, ... 6=Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + offset);
  return d;
}

export function mondayOfISO(iso: string): string {
  const dt = parseISO(iso);
  if (!dt) return iso;
  return isoDate(mondayOf(dt));
}

function shiftWeek(iso: string, weeks: number): string {
  const dt = parseISO(iso);
  if (!dt) return iso;
  return isoDate(new Date(dt.getTime() + weeks * WEEK_MS));
}

function formatWeek(iso: string): string {
  const dt = parseISO(iso);
  if (!dt) return iso;
  return dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function endOfWeekISO(weekStartIso: string): string {
  const dt = parseISO(weekStartIso);
  if (!dt) return weekStartIso;
  return isoDate(new Date(dt.getTime() + 6 * DAY_MS));
}

export function DiaryPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const driversQuery = useDiaryDrivers();
  const urlParamSecret = useAuthStore((s) => s.urlParamSecret);

  const [driverId, setDriverId] = useState<string>('');
  const [weekStart, setWeekStart] = useState<string>(() => isoDate(mondayOf(new Date())));
  const [submitting, setSubmitting] = useState(false);

  const todayMonday = useMemo(() => isoDate(mondayOf(new Date())), []);
  const maxWeek = useMemo(
    () => shiftWeek(todayMonday, MAX_WEEKS_AHEAD),
    [todayMonday],
  );

  // Default driver = first alphabetically once drivers load.
  useEffect(() => {
    if (driverId !== '' || !driversQuery.data || driversQuery.data.length === 0) return;
    const first = driversQuery.data[0];
    if (first) setDriverId(String(first.id));
  }, [driversQuery.data, driverId]);

  // Surface a toast on initial fetch failure.
  useEffect(() => {
    if (driversQuery.isError) toast.error('Could not load drivers. Please try again.');
  }, [driversQuery.isError, toast]);

  const driverOptions: SelectOption[] = useMemo(
    () =>
      (driversQuery.data ?? []).map((d) => ({
        value: String(d.id),
        label: d.name,
      })),
    [driversQuery.data],
  );

  const selectedDriver = useMemo(
    () =>
      driverId !== '' && driversQuery.data
        ? (driversQuery.data.find((d) => String(d.id) === driverId) ?? null)
        : null,
    [driverId, driversQuery.data],
  );
  const weekEnd = useMemo(() => (weekStart ? endOfWeekISO(weekStart) : null), [weekStart]);
  const holidayCheck = useDriverHolidayCheck(
    selectedDriver?.uuid ?? null,
    weekStart || null,
    weekEnd,
  );

  function handleWeekChange(value: string): void {
    setWeekStart(mondayOfISO(value));
  }

  function shift(weeks: number): void {
    const next = shiftWeek(weekStart, weeks);
    if (next > maxWeek) {
      toast.warning(`The diary supports up to ${MAX_WEEKS_AHEAD} weeks ahead.`);
      setWeekStart(maxWeek);
      return;
    }
    setWeekStart(next);
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!driverId) {
      toast.error('Pick a driver to continue.');
      return;
    }
    if (!weekStart) {
      toast.error('Pick a week to continue.');
      return;
    }
    if (!urlParamSecret) {
      toast.error('Could not sign the URL — try refreshing.');
      return;
    }
    setSubmitting(true);
    try {
      const encoded = await encryptParams(
        { driver_id: Number(driverId), week_start: mondayOfISO(weekStart) },
        urlParamSecret,
      );
      navigate(`/collections/diary/results?d=${encoded}`);
    } catch {
      toast.error('Could not encode the URL — try refreshing.');
    } finally {
      setSubmitting(false);
    }
  }

  // Render branches —
  if (driversQuery.isLoading) {
    return (
      <div className={styles.shell}>
        <PageHeader
          title="Booking Diary"
          subtitle="Search for and manage diary for the week"
          actions={<DiaryHeaderActions />}
        />
        <Card>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Spinner label="Loading drivers" />
          </div>
        </Card>
      </div>
    );
  }

  const drivers = driversQuery.data ?? [];

  if (drivers.length === 0) {
    return (
      <div className={styles.shell}>
        <PageHeader
          title="Booking Diary"
          subtitle="Search for and manage diary for the week"
          actions={<DiaryHeaderActions />}
        />
        <Card>
          <EmptyState
            title="No active drivers"
            description="Add a driver in Settings before scheduling diary jobs."
            action={
              <Link to="/admin/drivers" style={{ textDecoration: 'none' }}>
                <Button variant="primary">Go to Drivers</Button>
              </Link>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      <PageHeader
        title="Booking Diary"
        subtitle="Search for and manage diary for the week"
        actions={<DiaryHeaderActions />}
      />

      {holidayCheck.hasHoliday && selectedDriver !== null ? (
        <div role="alert" className={styles.holidayBanner}>
          <AlertTriangle size={18} aria-hidden />
          <div className={styles.holidayBannerBody}>
            <strong>{selectedDriver.name}</strong> is on holiday during this week:
            <ul className={styles.holidayList}>
              {holidayCheck.holidays.map((h) => (
                <li key={`${h.start_date}-${h.end_date}`}>
                  {h.start_date} to {h.end_date}
                  {h.period !== 'FULL' ? ` (${periodLabel(h.period)})` : ''}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <Card>
        <form className={styles.formGrid} onSubmit={handleSubmit} noValidate>
          <div className={styles.fieldsRow}>
            <Select
              label="Driver"
              required
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              options={driverOptions}
              placeholder="-- Select Driver --"
            />

            {/* The week field renders its own label + helper so the
                ◀ input ▶ controls can sit on a single, clean baseline.
                Letting <DatePicker> render its own label/helper inside
                this row pushes the chevron buttons below the input. */}
            <div className={styles.weekField}>
              <label htmlFor="week-start" className={styles.weekLabel}>
                Week starting
                <span className={styles.required} aria-hidden>
                  *
                </span>
              </label>
              <div className={styles.weekControls}>
                <Button
                  variant="ghost"
                  icon={ChevronLeft}
                  aria-label="Previous week"
                  onClick={() => shift(-1)}
                />
                <DatePicker
                  id="week-start"
                  value={weekStart}
                  onChange={(e) => handleWeekChange(e.target.value)}
                  maxDate={maxWeek}
                  required
                  className={styles.weekDatePicker}
                />
                <Button
                  variant="ghost"
                  icon={ChevronRight}
                  aria-label="Next week"
                  onClick={() => shift(1)}
                />
              </div>
              <p className={styles.weekHelper}>Week of {formatWeek(weekStart)}</p>
            </div>
          </div>

          <div className={styles.actions}>
            <Button type="submit" variant="primary" icon={CalendarDays} loading={submitting}>
              View Diary
            </Button>
            <Link to="/collections/diary/today" className={styles.todayLink}>
              View today's diary
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
